<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import BopModelViewer from '@/components/bop/BopModelViewer.vue'
import { GlbStatus } from '@/generated/graphql'
import type { MeshHint } from '@/composables/useMeshHighlighting'
import { ancestorsFromPath } from '@/composables/useStepMeshHints'

const props = defineProps<{
  ebomId: string
  articleNumber?: string
  articlePath?: string[]
}>()

type ViewerQuery = {
  article: {
    ebom: {
      id: string
      bopUpload: {
        id: string
        glbStatus: string
        glbUpload: { id: string; url: string } | null
      } | null
      ebomParts: Array<{ id: string; articleNumber: string }>
    }
  }
}

const { result, loading } = useQuery<ViewerQuery, { ebomId: string }>(
  gql`
    query EbomModelViewerData($ebomId: ID!) {
      article {
        ebom(id: $ebomId) {
          id
          bopUpload {
            id
            glbStatus
            glbUpload {
              id
              url
            }
          }
          ebomParts {
            id
            articleNumber
          }
        }
      }
    }
  `,
  () => ({ ebomId: props.ebomId }),
)

const bopUpload = computed(() => result.value?.article.ebom.bopUpload ?? null)

const knownArticleNames = computed<string[]>(() => {
  const parts = result.value?.article.ebom.ebomParts ?? []
  return [...new Set(parts.map((p) => p.articleNumber))]
})

const glbUrl = computed<string | null>(() => {
  const upload = bopUpload.value
  if (!upload || upload.glbStatus !== GlbStatus.Done) return null
  return upload.glbUpload?.url ?? null
})

const isConverting = computed(() => {
  const status = bopUpload.value?.glbStatus
  return status === GlbStatus.Queued || status === GlbStatus.Converting
})

const conversionFailed = computed(
  () => !isConverting.value && !glbUrl.value && bopUpload.value?.glbStatus === GlbStatus.Failed,
)

// A fresh [] in the template would count as a change on every render and redo the mesh matching.
const NO_VISIBLE_MESH_HINTS: MeshHint[] = []

const highlightedMeshHints = computed<MeshHint[]>(() => {
  if (!props.articleNumber) return []
  const path = props.articlePath ?? []
  const requiredAncestors = ancestorsFromPath(path)
  const glbArticleNames = Array.from(
    new Set([props.articleNumber, ...(path.length > 0 ? [path[path.length - 1]] : [])]),
  )
  return [{ articleNumber: props.articleNumber, glbArticleNames, requiredAncestors }]
})
</script>

<template>
  <bop-model-viewer
    :glb-url="glbUrl"
    :converting="isConverting"
    :conversion-failed="conversionFailed"
    :data-loading="loading && !glbUrl"
    :visible-mesh-hints="NO_VISIBLE_MESH_HINTS"
    :highlighted-mesh-hints="highlightedMeshHints"
    :known-article-names="knownArticleNames"
    hide-visibility-toggles
  />
</template>
